import * as THREE from 'three';
import type { Rect } from '../../core/math';
import { prefersTouchUi } from '../../input/touch';
import { onQualityTierKnown } from '../../render/qualityTier';
import { needForFirstView } from '../../render/shaderWarmup';
import { CLASSIC_BUILDINGS, DETAIL_FRAGMENT, DETAIL_FRAGMENT_HEAD, DETAIL_VERTEX, DETAIL_VERTEX_HEAD, loadBuildingDetail } from './buildingDetail';
import { ATLAS_SIZE, BAKED_FACADE, BAKED_GROUND, KERB_FACE, T, TILE_PX, createFacadeAtlas, createFacadeAtlasBaked, createGlowTexture, createGroundTextures, createGroundTexturesBaked, createSignAtlas, loadFacadeRelief, type GroundTextures, type SignAtlas } from './textures';

/**
 * Shared material set for the district. A small, fixed list: every static mesh in the city uses one
 * of these, which is what keeps draw calls low after merging.
 */
export interface CityMaterials {
  facade: THREE.MeshStandardMaterial;
  props: THREE.MeshStandardMaterial;
  metal: THREE.MeshStandardMaterial;
  glow: THREE.MeshBasicMaterial;
  asphalt: THREE.MeshStandardMaterial;
  setts: THREE.MeshStandardMaterial;
  paving: THREE.MeshStandardMaterial;
  yard: THREE.MeshStandardMaterial;
  grass: THREE.MeshStandardMaterial;
  gravel: THREE.MeshStandardMaterial;
  markings: THREE.MeshStandardMaterial;
  kerb: THREE.MeshStandardMaterial;
  water: THREE.MeshStandardMaterial;
  signs: THREE.MeshStandardMaterial;
  litSigns: THREE.MeshStandardMaterial;
  pools: THREE.MeshBasicMaterial;
  foliage: THREE.MeshStandardMaterial;
  signAtlas: SignAtlas;
  glowTexture: THREE.Texture;
  waterNormal: THREE.Texture;
  /** The water normal again at a puddle's scale (its own repeat/offset), for the puddle mesh. */
  puddleNormal: THREE.Texture;
  /** Grey (green-channel) soft mask for the puddle mesh's alphaMap. */
  puddleMask: THREE.Texture;
  /** Resolves when the baked ground set has loaded: `waterNormal`, `puddleNormal` and `puddleMask` then point at
   *  the files (flat placeholders before), and whoever holds them (the puddle mesh, the lamp streaks) rebinds. */
  ready: Promise<void>;
  /** Mood `windows` level (0.16 by day, 1 at dusk, 1.1 at night): gates the night-only window tiles. */
  setWindowLevel(windows: number): void;
  /** Per-frame animation (water scroll, flicker). */
  tick(time: number): void;
  /** Quality tier: HIGH gets the facade relief (bump from the atlas), the others a flat facade. */
  setTier(tier: 'low' | 'medium' | 'high'): void;
  /** Wet roads (0 dry … 1 soaked): the ground glossier and darker as the rain sets in. */
  setWetness(w: number): void;
  dispose(): void;
}

/**
 * Sample `map` at a world-space uv instead of the mesh's (the kerb's quads carry a 0..1 uv per segment, the
 * markings none that tiles): the ground plane's xz, or for a vertical face its along-face distance and height.
 * `scale` is 1 / the texture's repeat in metres. Needs the `vWorldPosCut` varying (cutWater).
 */
function worldUvMap(mat: THREE.Material, scale: number, faces: boolean): void {
  const prev = mat.onBeforeCompile;
  const prevKey = mat.customProgramCacheKey.bind(mat);
  mat.onBeforeCompile = (shader, renderer) => {
    prev.call(mat, shader, renderer);
    const uv = faces
      ? `vec3 bhFaceN = normalize( cross( dFdx( vWorldPosCut ), dFdy( vWorldPosCut ) ) );
  vec2 bhUv = ( abs( bhFaceN.y ) > 0.5 ? vWorldPosCut.xz : vec2( vWorldPosCut.x + vWorldPosCut.z, vWorldPosCut.y ) ) * ${scale.toFixed(4)};`
      : `vec2 bhUv = vWorldPosCut.xz * ${scale.toFixed(4)};`;
    // (the chunk is expanded after this hook, so the include itself is replaced)
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <map_fragment>',
      `#ifdef USE_MAP
  ${uv}
  diffuseColor *= texture2D( map, bhUv );
#endif`,
    );
  };
  mat.customProgramCacheKey = () => `${prevKey()}|worlduv`;
}

/**
 * The kerb's two sheets (ground_bake.py kerb / kerbface): the granite tile on the top at a world-space xz uv, and on
 * the face the strip of the face sheet (its v 0 … 0.5: 7.2 m along the kerb by the along-face distance, the gutter's
 * damp foot at the bottom) by the height. A quad that carries a uv of 2 + (u, v) (the gully gratings, streetLife)
 * takes that uv into the face sheet instead; one with a uv of 4 + (along, across) (the gutter band) takes the
 * sheet's gutter strip at `along` (in sheet repeats) and `across` (0 kerb … 1 road) through it. Both sheets are sampled and
 * one is picked, so the derivatives stay well defined. Needs the `vWorldPosCut` varying (cutWater).
 */
function kerbUvMap(mat: THREE.Material, uniforms: { uKerbFace: { value: THREE.Texture | null } }): void {
  const prev = mat.onBeforeCompile;
  const prevKey = mat.customProgramCacheKey.bind(mat);
  mat.onBeforeCompile = (shader, renderer) => {
    prev.call(mat, shader, renderer);
    Object.assign(shader.uniforms, uniforms);
    shader.fragmentShader = shader.fragmentShader.replace('#include <common>', '#include <common>\nuniform sampler2D uKerbFace;').replace(
      '#include <map_fragment>',
      `#ifdef USE_MAP
  vec3 bhFaceN = normalize( cross( dFdx( vWorldPosCut ), dFdy( vWorldPosCut ) ) );
  bool bhGrate = vMapUv.x >= 2.0;
  float bhAlong = ( vWorldPosCut.x + vWorldPosCut.z ) / ${KERB_FACE.along.toFixed(2)};
  vec2 bhUvTop = vWorldPosCut.xz * ${(1 / 0.9).toFixed(4)};
  vec2 bhUvFace = vMapUv.x >= 4.0 ? vec2( vMapUv.x - 4.0, ${KERB_FACE.gutter[0].toFixed(3)} + clamp( vMapUv.y - 4.0, 0.0, 1.0 ) * ${(KERB_FACE.gutter[1] - KERB_FACE.gutter[0]).toFixed(3)} )
    : bhGrate ? vMapUv - 2.0 : vec2( bhAlong, 0.005 + clamp( ( vWorldPosCut.y + 0.02 ) / ${KERB_FACE.height.toFixed(3)}, 0.0, 1.0 ) * 0.485 );
  vec4 bhTop = texture2D( map, bhUvTop );
  vec4 bhFace = texture2D( uKerbFace, bhUvFace );
  diffuseColor *= ( abs( bhFaceN.y ) > 0.5 && !bhGrate ) ? bhTop : bhFace;
#endif`,
    );
  };
  mat.customProgramCacheKey = () => `${prevKey()}|kerb`;
}

/** Discard fragments over water (canal/basin) so ground meshes can be simple polygons. */
function cutWater(mat: THREE.Material, water: Rect[], keep: Rect[]): void {
  const toV4 = (list: Rect[]): THREE.Vector4[] => {
    const v = list.map((r) => new THREE.Vector4(r.minX, r.minZ, r.maxX, r.maxZ));
    while (v.length < 4) v.push(new THREE.Vector4(1e6, 1e6, 1e6, 1e6));
    return v.slice(0, 4);
  };
  const rects = toV4(water);
  const keeps = toV4(keep);
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uWaterRects = { value: rects };
    shader.uniforms.uKeepRects = { value: keeps };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vWorldPosCut;')
      .replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvWorldPosCut = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vWorldPosCut;\nuniform vec4 uWaterRects[4];\nuniform vec4 uKeepRects[4];')
      .replace(
        'void main() {',
        `bool bhInRect(vec4 r) {
  return vWorldPosCut.x > r.x && vWorldPosCut.x < r.z && vWorldPosCut.z > r.y && vWorldPosCut.z < r.w;
}
void main() {
  bool bhKeep = false;
  for (int i = 0; i < 4; i++) if (bhInRect(uKeepRects[i])) bhKeep = true;
  if (!bhKeep) {
    for (int i = 0; i < 4; i++) if (bhInRect(uWaterRects[i])) discard;
  }`,
      );
  };
  mat.customProgramCacheKey = () => 'cutwater';
}

/** `keep`: rects inside the water that must still render (bridge decks). */
export function createCityMaterials(water: Rect[], keep: Rect[]): CityMaterials {
  const atlas = BAKED_FACADE ? createFacadeAtlasBaked() : createFacadeAtlas();
  let ground: GroundTextures = BAKED_GROUND ? createGroundTexturesBaked() : createGroundTextures();
  const glowTex = createGlowTexture();
  const signAtlas = createSignAtlas();

  const facade = new THREE.MeshStandardMaterial({
    map: atlas.map,
    emissiveMap: atlas.emissive,
    emissive: new THREE.Color(0xffd0a0),
    emissiveIntensity: 1.6,
    vertexColors: true,
    roughness: 0.82,
    metalness: 0.0,
    envMapIntensity: 0.35,
  });
  // Night-only tiles: the pub's big lit windows (etched panes, lamp-lit bar) glowed orange through an
  // overcast day at the day window level. By day they are plain dark glass with the etching showing; the
  // glow comes up with the dusk. The baked shop fronts (P2) are the same: by day the plate glass shows its
  // reflection and the goods behind it, at night the interior glows through. A few tile tests in the
  // shader, nothing per vertex.
  const facadeUniforms = { uNightTiles: { value: 1 }, uBhDetail: { value: null as THREE.DataArrayTexture | null }, uBhTileMean: { value: atlas.means } };
  const tilesPerRow = ATLAS_SIZE / TILE_PX;
  const nightTile = (id: number): string => {
    const col = id % tilesPerRow;
    const rowFromBottom = tilesPerRow - 1 - Math.floor(id / tilesPerRow);
    return `(bhTile.x == ${col.toFixed(1)} && bhTile.y == ${rowFromBottom.toFixed(1)})`;
  };
  facade.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, facadeUniforms);
    // building detail pass (buildingDetail.ts): world-space painted roof / wall textures on MEDIUM and HIGH
    shader.vertexShader = shader.vertexShader.replace('#include <common>', `#include <common>${DETAIL_VERTEX_HEAD}`).replace('#include <worldpos_vertex>', `#include <worldpos_vertex>${DETAIL_VERTEX}`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>${DETAIL_FRAGMENT_HEAD}`)
      .replace('#include <map_fragment>', `#include <map_fragment>${DETAIL_FRAGMENT}`);
    shader.fragmentShader = shader.fragmentShader.replace('#include <common>', '#include <common>\nuniform float uNightTiles;').replace(
      '#include <emissivemap_fragment>',
      `#include <emissivemap_fragment>
  {
    vec2 bhTile = floor( vEmissiveMapUv * ${tilesPerRow.toFixed(1)} );
    if ( ${[T.pubWinLit, T.pubDoor, T.shopWinLit, T.shopWinLit2, T.shopDoorLit, T.cafeWin, T.launderette, T.bookieWin].map(nightTile).join(' || ')} ) totalEmissiveRadiance *= uNightTiles;
  }`,
    );
    // the relief atlas (HIGH): its B channel is the baked occlusion of the reveals, sills and arches
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <aomap_fragment>',
      `#include <aomap_fragment>
#ifdef USE_NORMALMAP
  {
    float bhAo = texture2D( normalMap, vNormalMapUv ).b;
    reflectedLight.indirectDiffuse *= mix( 1.0, bhAo, 0.85 );
    reflectedLight.directDiffuse *= mix( 1.0, bhAo, 0.45 );
  }
#endif`,
    );
  };
  facade.customProgramCacheKey = () => `bhfacade${facade.defines?.BH_DETAIL !== undefined ? '|detail' : ''}${facade.normalMap ? '|relief' : ''}`;
  let detail: THREE.DataArrayTexture | null = null;
  /** The relief atlas (P4): fetched the first time HIGH is set, bound on HIGH only. */
  let relief: THREE.Texture | null = null;
  let reliefLoading = false;
  let disposed = false;
  const props = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, metalness: 0.15, envMapIntensity: 0.7 });
  const metal = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.45, metalness: 0.6, envMapIntensity: 0.9 });
  const glow = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: true });
  const foliage = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, metalness: 0, envMapIntensity: 0.15 });

  const groundMat = (map: THREE.Texture, rough: THREE.Texture | null, roughness: number, repeat: number, envI: number): THREE.MeshStandardMaterial => {
    // Builders emit world-space UVs (1 unit = 1 m); scale them through the texture repeat.
    map.repeat.set(1 / repeat, 1 / repeat);
    if (rough) rough.repeat.set(1 / (repeat * 1.37), 1 / (repeat * 1.37));
    const m = new THREE.MeshStandardMaterial({ map, roughness, metalness: 0.0, envMapIntensity: envI });
    if (rough) {
      m.roughnessMap = rough;
      m.roughness = 1;
    }
    cutWater(m, water, keep);
    return m;
  };
  const asphalt = groundMat(ground.asphalt, ground.asphaltRough, 0.6, 16, 1.25);
  // wheel tracks and tar patches (streetLife) shade the asphalt through vertex colours
  asphalt.vertexColors = true;
  const setts = groundMat(ground.setts, ground.settsRough, 0.55, 4, 1.15);
  const paving = groundMat(ground.paving, ground.pavingRough, 0.62, 6, 0.85);
  const yard = groundMat(ground.yard, ground.yardRough, 0.85, 12, 0.4);
  const grass = groundMat(ground.grass, null, 0.95, 8, 0.15);
  const gravel = groundMat(ground.gravel, null, 0.9, 4, 0.2);
  const markings = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.5, metalness: 0, envMapIntensity: 0.9, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  cutWater(markings, water, keep);
  const kerb = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.7, envMapIntensity: 0.6 });
  cutWater(kerb, water, keep);
  const kerbUniforms = { uKerbFace: { value: ground.kerbFace } };
  // The baked set (docs/PERF.md "Blender ground surfaces"): the kerb's granite on its top, the face sheet (the gutter
  // at its foot, the gully gratings) on its face, and the road paint's wear mask, all at a world-space uv (the
  // builders' vertex colours carry the tone); the roughness maps are baked with their albedo (puddles in the dips,
  // polished crowns), so they share its repeat.
  if (ground.kerb && ground.paintWear) {
    kerb.map = ground.kerb;
    kerb.roughness = 0.85;
    kerb.envMapIntensity = 0.45;
    kerbUvMap(kerb, kerbUniforms);
    markings.map = ground.paintWear;
    worldUvMap(markings, 1 / 2, false);
  }
  let tier: 'low' | 'medium' | 'high' = 'high';
  let tierKnown = !prefersTouchUi();
  /** Point the ground materials at the current set (placeholders first, the loaded files on `ready`). */
  const wireGround = (): void => {
    const fit = (t: THREE.Texture | null, repeat: number): THREE.Texture | null => {
      t?.repeat.set(1 / repeat, 1 / repeat);
      return t;
    };
    const baked = !!ground.kerb;
    asphalt.map = fit(ground.asphalt, 16);
    asphalt.roughnessMap = fit(ground.asphaltRough, baked ? 16 : 16 * 1.37);
    setts.map = fit(ground.setts, 4);
    setts.roughnessMap = fit(ground.settsRough, baked ? 4 : 4 * 1.37);
    paving.map = fit(ground.paving, 6);
    paving.roughnessMap = fit(ground.pavingRough, 6);
    yard.map = fit(ground.yard, 12);
    yard.roughnessMap = fit(ground.yardRough, 12);
    grass.map = fit(ground.grass, 8);
    gravel.map = fit(ground.gravel, 4);
    if (ground.kerb) kerb.map = ground.kerb;
    kerbUniforms.uKerbFace.value = ground.kerbFace;
    if (ground.paintWear) markings.map = ground.paintWear;
    // the relief of the joints: HIGH only (a sample and a tangent frame per fragment)
    const relief = tier === 'high';
    const sn = relief ? fit(ground.settsNormal, 4) : null;
    const pn = relief ? fit(ground.pavingNormal, 6) : null;
    for (const [m, n, s] of [
      [setts, sn, 0.8],
      [paving, pn, 0.7],
    ] as const) {
      if (m.normalMap !== n) {
        // (a recompile only when the map comes or goes; the loaded file replacing its placeholder is a swap)
        if (!m.normalMap !== !n) m.needsUpdate = true;
        m.normalMap = n;
        m.normalScale.set(s, s);
      }
    }
  };
  wireGround();
  let wetness = 0;
  const applyWetness = (): void => {
    const w = wetness;
    asphalt.roughness = 1.4 - 0.6 * w;
    asphalt.envMapIntensity = 0.75 + 0.75 * w;
    asphalt.color.setScalar(1 - 0.16 * w);
    setts.roughness = 1.35 - 0.55 * w;
    setts.envMapIntensity = 0.7 + 0.7 * w;
    setts.color.setScalar(1 - 0.14 * w);
    // (with the baked roughness map the factor scales it; without, it is the roughness)
    paving.roughness = (paving.roughnessMap ? 1.15 : 0.85) - 0.3 * w;
    paving.color.setScalar(1 - 0.12 * w);
    if (yard.roughnessMap) {
      yard.roughness = 1.1 - 0.35 * w;
      yard.color.setScalar(1 - 0.1 * w);
    }
  };
  applyWetness();

  // the ripple (7 m on the canal; the puddle mesh has it again at its own scale) and the puddle alpha come with the
  // baked ground: flat placeholders until then, see `ready`
  const fitWater = (t: THREE.Texture): THREE.Texture => {
    t.repeat.set(1 / 7, 1 / 7);
    return t;
  };
  const puddleClone = (t: THREE.Texture): THREE.Texture => {
    const p = t.clone();
    p.repeat.set(1.5, 1.5);
    p.needsUpdate = true;
    return p;
  };
  let waterNormal = fitWater(ground.waterNormal);
  let puddleNormal = puddleClone(waterNormal);
  let puddleMask = ground.puddleMask;
  const waterMat = new THREE.MeshStandardMaterial({
    color: 0x1e3434,
    roughness: 0.3,
    metalness: 0.1,
    normalMap: waterNormal,
    normalScale: new THREE.Vector2(0.11, 0.11),
    envMapIntensity: 2.6,
  });
  // Moving water: a second, finer ripple layer scrolled the other way is blended into the normal, and the
  // water darkens a little in the troughs, so the canal and basin shimmer instead of sliding one pattern.
  const waterUniforms = { uWaterT: { value: 0 } };
  waterMat.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, waterUniforms);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform float uWaterT;')
      .replace(
        'vec3 mapN = texture2D( normalMap, vNormalMapUv ).xyz * 2.0 - 1.0;',
        `vec3 mapN = texture2D( normalMap, vNormalMapUv ).xyz * 2.0 - 1.0;
  // the two extra layers are rotated off the base layer's axes, so the three sets of plane waves never
  // line up into a lattice under a lamp
  vec2 uvA = vNormalMapUv * 2.9;
  uvA = vec2( uvA.x * 0.83 - uvA.y * 0.56, uvA.x * 0.56 + uvA.y * 0.83 ) + vec2( uWaterT * 0.021, -uWaterT * 0.017 );
  vec2 uvB = vNormalMapUv * 0.37;
  uvB = vec2( uvB.x * 0.93 + uvB.y * 0.37, -uvB.x * 0.37 + uvB.y * 0.93 ) + vec2( -uWaterT * 0.004, uWaterT * 0.006 );
  vec3 mapN2 = texture2D( normalMap, uvA ).xyz * 2.0 - 1.0;
  vec3 mapN3 = texture2D( normalMap, uvB ).xyz * 2.0 - 1.0;
  mapN2.xy = vec2( mapN2.x * 0.83 + mapN2.y * 0.56, -mapN2.x * 0.56 + mapN2.y * 0.83 );
  mapN3.xy = vec2( mapN3.x * 0.93 - mapN3.y * 0.37, mapN3.x * 0.37 + mapN3.y * 0.93 );
  mapN.xy = mapN.xy + mapN2.xy * 0.38 + mapN3.xy * 1.1;
  diffuseColor.rgb *= 0.86 + 0.14 * mapN3.z;`,
      );
  };
  waterMat.customProgramCacheKey = () => 'bhwater';

  const signs = new THREE.MeshStandardMaterial({
    map: signAtlas.texture,
    emissiveMap: signAtlas.texture,
    emissive: new THREE.Color(0xffffff),
    emissiveIntensity: 0.28,
    alphaTest: 0.35,
    roughness: 0.6,
    side: THREE.DoubleSide,
  });
  const litSigns = new THREE.MeshStandardMaterial({
    map: signAtlas.texture,
    emissiveMap: signAtlas.texture,
    emissive: new THREE.Color(0xffffff),
    emissiveIntensity: 1.6,
    alphaTest: 0.35,
    roughness: 0.6,
    side: THREE.DoubleSide,
  });
  const pools = new THREE.MeshBasicMaterial({
    map: glowTex,
    vertexColors: true,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
  });

  const all: THREE.Material[] = [facade, props, metal, glow, asphalt, setts, paving, yard, grass, gravel, markings, kerb, waterMat, signs, litSigns, pools, foliage];
  const groundList = (g: GroundTextures): THREE.Texture[] => [g.asphalt, g.asphaltRough, g.setts, g.settsRough, g.paving, g.yard, g.grass, g.gravel, g.pavingRough, g.yardRough, g.settsNormal, g.pavingNormal, g.kerb, g.paintWear, g.kerbFace, g.waterNormal, g.puddleMask].filter((t): t is THREE.Texture => !!t);
  const textures: THREE.Texture[] = [atlas.map, atlas.emissive, atlas.means, ...groundList(ground), glowTex, puddleNormal, signAtlas.texture];
  const applyTier = (): void => {
    // Relief (docs/research/BLENDER-BUILDINGS.md P4): the baked normal + occlusion atlas (textures.ts
    // loadFacadeRelief), bound as a packed normal map on HIGH only (one sample and a tangent frame per fragment).
    // It replaced the bump from the atlas's own luminance; MEDIUM and LOW stay flat as they were.
    if (tier === 'high' && relief === null && !reliefLoading && tierKnown) {
      reliefLoading = true;
      const loading = loadFacadeRelief();
      needForFirstView(loading);
      void loading.then((t) => {
        if (disposed || !t) {
          t?.dispose();
          return;
        }
        relief = t;
        textures.push(t);
        applyTier();
      });
    }
    const wantRelief = tier === 'high' ? relief : null;
    if (facade.normalMap !== wantRelief) {
      facade.normalMap = wantRelief;
      facade.normalScale.set(1, 1);
      facade.defines = { ...(facade.defines ?? {}) };
      if (wantRelief) facade.defines.USE_PACKED_NORMALMAP = '';
      else delete facade.defines.USE_PACKED_NORMALMAP;
      facade.needsUpdate = true;
    }
    // painted roof / wall detail (buildingDetail.ts): MEDIUM and HIGH, once the sheet has loaded
    const wantDetail = tier !== 'low' && detail !== null;
    const hasDetail = facade.defines?.BH_DETAIL !== undefined;
    if (wantDetail !== hasDetail) {
      facade.defines = { ...(facade.defines ?? {}) };
      if (wantDetail) facade.defines.BH_DETAIL = '';
      else delete facade.defines.BH_DETAIL;
      facade.needsUpdate = true;
    }
    wireGround();
  };
  applyTier();
  // Touch devices start at MEDIUM (ui applyGraphics), but the tier reads HIGH until the render system has
  // published it: the HIGH-only relief (400 KB) waits for the real tier there, instead of competing with the
  // first view's textures for a phone's bandwidth and then going unused.
  if (!tierKnown) onQualityTierKnown(() => {
    tierKnown = true;
    if (!disposed) applyTier();
  });
  // the baked ground arrives after the first frames: same sampler kinds, so no recompile, just the maps
  const ready = (ground.ready ?? Promise.resolve(ground)).then((loaded) => {
    if (disposed) {
      for (const t of groundList(loaded)) t.dispose();
      return;
    }
    ground = loaded;
    textures.push(...groundList(loaded));
    wireGround();
    applyWetness();
    if (loaded.waterNormal !== waterNormal) {
      waterNormal = fitWater(loaded.waterNormal);
      puddleNormal = puddleClone(waterNormal);
      puddleMask = loaded.puddleMask;
      textures.push(puddleNormal);
      waterMat.normalMap = waterNormal;
      mats.waterNormal = waterNormal;
      mats.puddleNormal = puddleNormal;
      mats.puddleMask = puddleMask;
    }
  });
  needForFirstView(ready);
  if (atlas.ready) needForFirstView(atlas.ready);
  // the baked atlas arrives after the first frames: same sampler kinds, so no recompile, just the uniforms
  void atlas.ready?.then((loaded) => {
    if (disposed) {
      loaded.map.dispose();
      loaded.emissive.dispose();
      return;
    }
    atlas.map = loaded.map;
    atlas.emissive = loaded.emissive;
    atlas.means = loaded.means;
    facade.map = loaded.map;
    facade.emissiveMap = loaded.emissive;
    facadeUniforms.uBhTileMean.value = loaded.means;
    textures.push(loaded.map, loaded.emissive, loaded.means);
  });
  if (!CLASSIC_BUILDINGS) {
    const detailLoad = loadBuildingDetail();
    needForFirstView(detailLoad);
    void detailLoad.then((t) => {
      if (!t) return;
      if (disposed) return void t.dispose();
      detail = t;
      facadeUniforms.uBhDetail.value = t;
      applyTier();
    });
  }

  const mats: CityMaterials = {
    facade,
    props,
    metal,
    glow,
    asphalt,
    setts,
    paving,
    yard,
    grass,
    gravel,
    markings,
    kerb,
    water: waterMat,
    signs,
    litSigns,
    pools,
    foliage,
    signAtlas,
    glowTexture: glowTex,
    waterNormal,
    puddleNormal,
    puddleMask,
    ready,
    setWindowLevel(windows) {
      // day (0.16) -> 0.1 of the lit glow, dusk (1) and night -> full
      const k = Math.min(1, Math.max(0, (windows - 0.2) / 0.6));
      facadeUniforms.uNightTiles.value = 0.1 + 0.9 * k * k * (3 - 2 * k);
    },
    tick(time) {
      waterNormal.offset.set(time * 0.006, time * 0.011);
      puddleNormal.offset.set(time * 0.03, -time * 0.02);
      waterUniforms.uWaterT.value = time;
    },
    setTier(t) {
      tier = t;
      applyTier();
    },
    setWetness(w) {
      wetness = w;
      applyWetness();
    },
    dispose() {
      for (const m of all) m.dispose();
      for (const t of textures) t.dispose();
      detail?.dispose();
      disposed = true;
    },
  };
  return mats;
}
